/**
 * Field provenance helper. A mapper field reference is either a bare source
 * header ("Color") or a processed/workbook output reference ("out:Handle").
 * Pure + server-safe so both editors and the backend tree can import it.
 */
export type FieldOrigin = "source" | "workbook";

export function describeField(value: string): { name: string; origin: FieldOrigin } {
  const v = (value ?? "").trim();
  if (v.toLowerCase().startsWith("out:")) {
    return { name: v.slice(4).trim(), origin: "workbook" };
  }
  return { name: v, origin: "source" };
}

export type AvailableField = { name: string; origin: FieldOrigin };

/**
 * The single source of truth for which fields a module parameter can reference:
 * every uploaded-file column (origin "source") followed by every already-
 * processed workbook column (origin "workbook"). Both the {{}} autocomplete and
 * the FieldPicker dropdown build from this so field selection behaves
 * identically across all modules.
 */
export function availableFields(
  sourceHeaders: string[],
  outputColumns: string[],
): AvailableField[] {
  return [
    ...sourceHeaders.map((name) => ({ name, origin: "source" as const })),
    ...outputColumns.map((name) => ({ name, origin: "workbook" as const })),
  ];
}

/**
 * The token used to reference a field: the bare name for a source column, or
 * `out:Name` for a workbook (processed) column.
 */
export function fieldToken(field: AvailableField): string {
  return field.origin === "workbook" ? `out:${field.name}` : field.name;
}
